import { ShieldCheck, Hash, Fingerprint, ArrowLeftRight, BatteryCharging, Zap, Gauge, RotateCw, Repeat, Network, Cable, ShieldAlert, Cpu, Terminal, Flame, Wrench, Thermometer, Binary, Octagon, Settings2, KeyRound, GaugeCircle, Mountain, CircleDot, Wind, ScanSearch, TrendingDown, BatteryMedium, ListChecks, FileCode2, Disc3, Truck, Fuel, Cog } from 'lucide-react';

// `addedDate` (ISO date) is used only as a Trending tie-breaker (most recently added
// first) when visit counts are equal or absent — this catalog has no other date field.
// Set to each tool's actual ship date; new entries should get today's date. Omitted on
// `comingSoon` stubs since they have no route and are excluded from ALL_TOOLS below.
export const TOOL_CATEGORIES = [
  {
    id: 'autosar-e2e',
    title: 'AUTOSAR E2E Tools',
    tools: [
      {
        icon: ShieldCheck,
        title: 'E2E Calculator',
        to: '/tools/autosar-e2e',
        description: 'Compute or verify CRC and Counter fields for AUTOSAR E2E Profiles 1, 2, 4, and 5.',
        addedDate: '2026-07-05',
      },
      {
        icon: Hash,
        title: 'CRC Calculator',
        to: '/tools/crc-calculator',
        description: 'Compute standard CRC checksums over arbitrary hex byte sequences.',
        addedDate: '2026-07-06',
      },
      {
        icon: ArrowLeftRight,
        title: 'Data Converter',
        to: '/tools/data-converter',
        description: 'Convert between hex, decimal, binary, and byte-level representations.',
        addedDate: '2026-07-07',
      },
      {
        icon: Fingerprint,
        title: 'Data ID Calculator',
        to: '/tools/data-id-calculator',
        description: 'See how AUTOSAR E2E Profile 1 folds a 16-bit Data ID into the CRC across all four inclusion modes.',
        addedDate: '2026-07-08',
      },
    ],
  },
  {
    id: 'powertrain',
    title: 'Powertrain Tools',
    tools: [
      {
        icon: BatteryCharging,
        title: 'Battery Sizing Tool',
        to: '/tools/ev-battery-sizing',
        description: 'Size pack cell configuration, energy, current capability, and mass from range, consumption, motor power, cell specs, SOC window, and EOL margin.',
        addedDate: '2026-07-10',
      },
      {
        icon: Zap,
        title: 'Motor Sizing Tool',
        to: '/tools/ev-motor-sizing',
        description: 'Estimate continuous and peak motor power, torque, base and max RPM, and DC and phase current from vehicle mass, aerodynamics, traction limit, acceleration, top-speed, and grade targets.',
        addedDate: '2026-07-11',
      },
      {
        icon: RotateCw,
        title: 'Vehicle Speed to RPM/RPS',
        to: '/tools/vehicle-speed-rpm',
        description: 'Convert vehicle speed (km/h or m/s) to wheel and motor/engine shaft rotational speed, in both RPM and RPS.',
        addedDate: '2026-07-12',
      },
      {
        icon: Settings2,
        title: 'Gear Ratio & Final Drive Ratio Calculator',
        to: '/tools/gear-ratio-calculator',
        description: 'Calculate and visualize how transmission gear ratios and final drive ratio relate engine/motor RPM to wheel speed and vehicle speed, with a per-gear results table, speed-vs-RPM chart, final drive comparison, and an Advanced Mode for wheel torque and tractive force.',
        addedDate: '2026-08-18',
      },
      {
        icon: GaugeCircle,
        title: 'Torque-Power-RPM Calculator',
        to: '/tools/torque-power-rpm-calculator',
        description: 'Calculate torque, RPM, or power from the other two, with automatic unit conversion (kW/W/HP/PS, N·m/lb-ft) and a live torque-vs-power-vs-RPM relationship chart.',
        addedDate: '2026-08-24',
      },
      {
        icon: Disc3,
        title: 'Tyre Size & Speedometer Error Calculator',
        to: '/tools/tyre-size-calculator',
        description: 'Compare an original and replacement tyre size: interactive to-scale tyre diagrams, diameter and ride-height change, speedometer and odometer error with a UNECE R39 check, and the new rolling radius.',
        addedDate: '2026-09-30',
      },
      {
        icon: Cog,
        title: 'Planetary Power-Split (eCVT) Calculator',
        to: '/tools/powertrain/power-split-calculator',
        description: 'Learn how a power-split hybrid (Toyota THS-style eCVT) works: solve planetary speeds and torques, MG1/MG2 and battery power, and the mechanical vs electrical path, with an animated architecture diagram, lever diagram, gear cutaway and driving-mode explorer.',
        addedDate: '2026-10-04',
      },
    ],
  },
  {
    id: 'power-electronics',
    title: 'Power Electronics',
    tools: [
      {
        icon: Cable,
        title: 'Cable Size Calculator',
        to: '/tools/cable-size-calculator',
        description: 'Recommend the minimum cable cross-section for a DC, single-phase, or three-phase circuit from load current, voltage drop, run length, and conductor material.',
        addedDate: '2026-07-20',
      },
      {
        icon: ShieldAlert,
        title: 'Fuse Rating Calculator',
        to: '/tools/power-electronics/fuse-rating-calculator',
        description: 'Recommend wire gauge and fuse rating for an LV automotive or EV/HV circuit, with ampacity, voltage-drop, breaking-capacity, and wire/fuse coordination checks.',
        addedDate: '2026-08-01',
      },
      {
        icon: Cpu,
        title: 'DC-DC Converter Sizing Tool',
        to: '/tools/dcdc-converter-sizing-calculator',
        description: 'Derive a defensible minimum DC-DC converter spec from a vehicle load list and source voltage behavior, with a full editable margin waterfall, voltage-window feasibility check, and sensitivity view.',
        addedDate: '2026-08-02',
      },
      {
        icon: TrendingDown,
        title: 'Voltage Drop Calculator',
        to: '/tools/voltage-drop-calculator',
        description: 'Analyze voltage drop across an automotive circuit — cable, connector, fuse/relay, and ground — with a Battery→Load breakdown, pass/fail against your allowable limit, and the minimum cable size that meets it.',
        addedDate: '2026-09-16',
      },
      {
        icon: BatteryMedium,
        title: 'HV Precharge Resistor Calculator',
        to: '/tools/precharge-resistor-calculator',
        description: 'Size or check an EV HV precharge resistor from the X/Y capacitance of every e-powertrain component on the bus, with tolerance corners, resistor stresses, contactor inrush, a Y-capacitance 0.2 J check, and the precharge curve.',
        addedDate: '2026-09-26',
      },
      {
        icon: Zap,
        title: 'HV Busbar Sizing Calculator',
        to: '/tools/busbar-sizing-calculator',
        description: 'Check or size a rectangular copper or aluminium busbar for EV/HV power electronics: heat-balance temperature rise, peak and duty-cycle heating, IEC 60949 short-circuit withstand, skin effect, loss and voltage drop, cross-checked against DIN 43671.',
        addedDate: '2026-10-01',
      },
    ],
  },
  {
    id: 'diagnostic',
    title: 'Diagnostic',
    tools: [
      {
        icon: Terminal,
        title: 'UDS Diagnostic Session Simulator',
        to: '/tools/diagnostic/uds-simulator',
        description: 'Run a stateful UDS (ISO 14229) request/response session against a virtual ECU, with session control, security access, DID read/write, and DTC memory.',
        addedDate: '2026-08-03',
      },
      {
        icon: KeyRound,
        title: 'Security Access Seed-Key Simulator',
        to: '/tools/diagnostic/security-access-simulator',
        description: 'Practice the UDS (ISO 14229) 0x27 SecurityAccess seed->key->verification handshake with a visual byte-by-byte trace, fictional training algorithms, and attempt lockout — not a real ECU unlock algorithm.',
        addedDate: '2026-08-20',
      },
      {
        icon: ScanSearch,
        title: 'NRC Decoder',
        to: '/tools/diagnostic/nrc-decoder',
        description: 'Decode UDS (ISO 14229) Negative Response Codes from a bare NRC or a full 7F/SID/NRC response, with meanings, causes, troubleshooting steps, and a searchable reference table.',
        addedDate: '2026-09-16',
      },
      {
        icon: ListChecks,
        title: 'DTC & Status Byte Decoder',
        to: '/tools/diagnostic/dtc-decoder',
        description: 'Decode DTCs (P0301 <-> bytes, Failure Type Byte), the ISO 14229-1 DTC status byte with a bit editor and lifecycle simulator, full UDS 0x19 ReadDTCInformation responses, and OBD-II Mode 01/03/07/0A/09 responses with formulas shown.',
        addedDate: '2026-09-26',
      },
    ],
  },
  {
    id: 'vehicle-dynamics',
    title: 'Vehicle Dynamics',
    tools: [
      {
        icon: Gauge,
        title: 'Traction Power Calculator',
        to: '/tools/traction-power',
        description: 'Estimate the traction force and power required at the wheels from vehicle speed, road gradient, acceleration, mass, aerodynamics, and rolling resistance.',
        addedDate: '2026-07-13',
      },
      {
        icon: Octagon,
        title: 'Braking Distance Calculator',
        to: '/tools/braking-distance-calculator',
        description: 'Estimate reaction, braking, and total stopping distance from vehicle mass, speed, road friction, and gradient, with an Advanced mode covering dynamic front/rear weight transfer and brake-system-torque limits. Regenerative braking excluded.',
        addedDate: '2026-08-18',
      },
      {
        icon: Mountain,
        title: 'Gradient Force Calculator',
        to: '/tools/gradient-force-calculator',
        description: 'Calculate the Gradient Force (Grade Resistance) acting along a road gradient from vehicle mass and road grade (% or degrees), with optional wheel torque and power, grade reference tables, and a live vehicle-on-slope diagram.',
        addedDate: '2026-08-24',
      },
      {
        icon: CircleDot,
        title: 'Rolling Resistance Force Calculator',
        to: '/tools/rolling-resistance-calculator',
        description: 'Calculate rolling resistance force, power and Wh/km from vehicle mass, tyre class (passenger to truck) and road surface or a custom Crr, with EU tyre-label and sensitivity reference tables.',
        addedDate: '2026-08-24',
      },
      {
        icon: Wind,
        title: 'Aerodynamic Force Calculator',
        to: '/tools/aerodynamic-force-calculator',
        description: 'Calculate aerodynamic drag force and power from vehicle speed, drag coefficient, frontal area, air density, and headwind/tailwind/crosswind, with an interactive Speed vs Drag Force chart.',
        addedDate: '2026-08-24',
      },
    ],
  },
  {
    id: 'conversion',
    title: 'Conversion',
    tools: [
      {
        icon: Repeat,
        title: 'RPM to Rad/s Converter',
        to: '/tools/rpm-rad-converter',
        description: 'Convert rotational speed between RPM, RPS, radians per second, and degrees per second, with live results, formulas, and adjustable precision.',
        addedDate: '2026-07-14',
      },
      {
        icon: Flame,
        title: 'kW ↔ HP Calculator',
        to: '/tools/kw-hp-calculator',
        description: 'Convert power between Watts, Kilowatts, and mechanical Horsepower, with live results, formulas, and adjustable precision.',
        addedDate: '2026-08-13',
      },
      {
        icon: Wrench,
        title: 'Nm ↔ lb-ft Calculator',
        to: '/tools/torque-calculator',
        description: 'Convert torque between N·m, lb-ft, lb-in, kgf·m, kgf·cm, oz-in, and dyn·cm, with live results, formulas, and adjustable precision.',
        addedDate: '2026-08-13',
      },
      {
        icon: Gauge,
        title: 'PSI ↔ Bar Calculator',
        to: '/tools/pressure-calculator',
        description: 'Convert pressure between psi, bar, kPa, and atm, with live results, formulas, and adjustable precision.',
        addedDate: '2026-08-13',
      },
      {
        icon: Gauge,
        title: 'mph ↔ km/h Calculator',
        to: '/tools/speed-calculator',
        description: 'Convert speed between mph, km/h, and m/s, with live results, formulas, and adjustable precision.',
        addedDate: '2026-08-14',
      },
      {
        icon: Thermometer,
        title: 'Celsius ↔ Kelvin Calculator',
        to: '/tools/temperature-calculator',
        description: 'Convert temperature between Celsius, Kelvin, and Fahrenheit, with live results, formulas, adjustable precision, and absolute-zero limits.',
        addedDate: '2026-08-14',
      },
      {
        icon: Fuel,
        title: 'Fuel Consumption & CO₂ Converter',
        to: '/tools/fuel-consumption-calculator',
        description: 'Convert fuel economy (L/100 km, km/L, mpg US/UK, CNG kg/100 km) and EV efficiency (kWh/100 km, Wh/km, km/kWh, mi/kWh, MPGe), with tank-to-wheel CO₂ per km for each fuel and grid-intensity presets.',
        addedDate: '2026-10-04',
      },
    ],
  },
  {
    id: 'communication',
    title: 'Communication & Bus Tools',
    tools: [
      {
        icon: Network,
        title: 'CAN Bus Load Calculator',
        to: '/tools/can-bus-load-calculator',
        description: 'Build a table of periodic CAN messages and see the resulting bus load % update live, with per-message bandwidth breakdown and headroom status.',
        addedDate: '2026-07-15',
      },
      {
        icon: Binary,
        title: 'CAN Endianness Visualizer',
        to: '/tools/can-endianness-visualizer',
        description: 'Visualize how Intel (Little Endian) and Motorola (Big Endian) CAN signals pack into data bytes, with an interactive bit grid, live decode, and DBC output.',
        addedDate: '2026-08-18',
      },
      {
        icon: FileCode2,
        title: 'CAN Raw Frame Decoder',
        to: '/tools/can-frame-decoder',
        description: 'Decode raw CAN and CAN FD frames (0-64 bytes) into physical signal values with Intel/Motorola bit visualization, then generate one DBC for all frames, validate it, and download the .dbc file.',
        addedDate: '2026-09-27',
      },
      {
        icon: Truck,
        title: 'J1939 Frame Decoder & DBC Generator',
        to: '/tools/j1939-frame-decoder',
        description: 'Break a 29-bit J1939 CAN ID into priority, PGN (PDU1/PDU2), destination and source address, decode SPNs with error / not-available flags and a bit map, then generate, validate and download a J1939 DBC.',
        addedDate: '2026-09-30',
      },
    ],
  },
];

// Flattened list for search/lookup, each tool tagged with its parent category label.
// "Coming soon" stubs have no route, so they're excluded — search results must
// always be navigable.
export const ALL_TOOLS = TOOL_CATEGORIES.flatMap((category) =>
  category.tools.filter((tool) => !tool.comingSoon).map((tool) => ({ ...tool, category: category.title }))
);

// Maps a tool into the shared TrendingSection item shape (see TrendingSection.jsx).
export const toolToTrendingItem = (tool) => ({
  id: tool.to,
  to: tool.to,
  title: tool.title,
  description: tool.description,
  badge: tool.category,
  icon: tool.icon,
  updatedAt: tool.addedDate,
});
